import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { useUser } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  saveSnippet,
  getListMySnippetsQueryKey,
} from "@workspace/api-client-react";
import type { CodeSnippet } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { showRateLimitToast } from "@/lib/apiErrors";
import { Save, Share2, RotateCcw, LogIn, Check, Copy } from "lucide-react";
import { basePath } from "@/lib/clerk";
import { PythonSandbox } from "./python-sandbox";
import {
  WebSandbox,
  parseWebStarter,
  serializeWebFiles,
} from "./web-sandbox";
import type { SandpackFiles } from "@codesandbox/sandpack-react";

type Lang = "python" | "web";

export function CodeSandbox(props: {
  language: Lang;
  starter: string;
  lessonId?: number;
  lessonTitle?: string;
  initialSnippet?: CodeSnippet | null;
  /**
   * When true, the editor is locked (no save/share/reset) but the user can
   * still **run** the code. Used by /s/:id share pages so visitors can
   * execute snippets without modifying them.
   */
  readOnly?: boolean;
}) {
  const {
    language,
    starter,
    lessonId,
    lessonTitle,
    initialSnippet,
    readOnly,
  } = props;
  const { isSignedIn } = useUser();
  const { toast } = useToast();
  const qc = useQueryClient();

  // Python state
  const initialPython =
    language === "python" ? (initialSnippet?.code ?? starter) : "";
  const [pyCode, setPyCode] = useState(initialPython);

  // Web state
  const initialWebFiles: SandpackFiles = useMemo(() => {
    if (language !== "web") return {};
    const source = initialSnippet?.code ?? starter;
    return parseWebStarter(source);
  }, [language, initialSnippet?.code, starter]);
  const [webFiles, setWebFiles] = useState<SandpackFiles>(initialWebFiles);
  const [webResetKey, setWebResetKey] = useState(0);

  useEffect(() => {
    if (language === "python")
      setPyCode(initialSnippet?.code ?? starter);
    else setWebFiles(parseWebStarter(initialSnippet?.code ?? starter));
  }, [initialSnippet?.id, initialSnippet?.code, starter, language]);

  const [saving, setSaving] = useState(false);
  const [savedSnippet, setSavedSnippet] = useState<CodeSnippet | null>(
    initialSnippet ?? null,
  );
  const [shareCopied, setShareCopied] = useState(false);

  const reset = () => {
    if (language === "python") setPyCode(starter);
    else {
      setWebFiles(parseWebStarter(starter));
      setWebResetKey((k) => k + 1);
    }
  };

  const persist = useCallback(async (): Promise<CodeSnippet | null> => {
    if (!isSignedIn) return null;
    const code =
      language === "python" ? pyCode : serializeWebFiles(webFiles);
    const title = lessonTitle
      ? `${lessonTitle}`
      : language === "python"
        ? "Untitled Python snippet"
        : "Untitled web snippet";
    const saved = await saveSnippet({
      lessonId: lessonId ?? null,
      language,
      title,
      code,
    });
    setSavedSnippet(saved);
    qc.invalidateQueries({ queryKey: getListMySnippetsQueryKey() });
    return saved;
  }, [isSignedIn, language, pyCode, webFiles, lessonId, lessonTitle, qc]);

  const handleSave = useCallback(async () => {
    if (!isSignedIn || saving) return;
    setSaving(true);
    try {
      await persist();
      toast({
        title: "Saved",
        description: "Your code is saved to your account.",
      });
    } catch (e) {
      if (showRateLimitToast(e)) return;
      toast({
        title: "Couldn't save",
        description: e instanceof Error ? e.message : "Try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [isSignedIn, saving, persist, toast]);

  const handleShare = async () => {
    if (!isSignedIn || saving) return;
    setSaving(true);
    try {
      const snippet = await persist();
      if (!snippet) return;
      const url = `${window.location.origin}${basePath}/s/${snippet.id}`;
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2200);
        toast({ title: "Link copied", description: url });
      } catch {
        toast({ title: "Share link", description: url });
      }
    } catch (e) {
      if (showRateLimitToast(e)) return;
      toast({
        title: "Couldn't share",
        description: e instanceof Error ? e.message : "Try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground mr-2">
          // {readOnly ? "shared snippet · run only" : "try it in the sandbox"}
        </span>
        <div className="flex-1" />
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={reset}
            className="h-9"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
          </Button>
        )}
        {!readOnly && isSignedIn && (
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="h-9"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
        {!readOnly && !isSignedIn && (
          <Button asChild type="button" size="sm" variant="secondary" className="h-9">
            <Link href="/sign-in">
              <LogIn className="w-3.5 h-3.5 mr-1.5" /> Sign in to save
            </Link>
          </Button>
        )}
        {!readOnly && isSignedIn && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleShare}
            className="h-9"
          >
            {shareCopied ? (
              <Check className="w-3.5 h-3.5 mr-1.5" />
            ) : (
              <Share2 className="w-3.5 h-3.5 mr-1.5" />
            )}
            {shareCopied ? "Copied!" : "Share link"}
          </Button>
        )}
        {readOnly && savedSnippet && (
          <ShareUrl snippetId={savedSnippet.id} />
        )}
      </div>

      {language === "python" ? (
        <PythonSandbox
          code={pyCode}
          onChange={readOnly ? undefined : setPyCode}
          editorReadOnly={readOnly}
        />
      ) : (
        <WebSandbox
          files={webFiles}
          onFilesChange={readOnly ? undefined : setWebFiles}
          readOnly={readOnly}
          remountKey={webResetKey}
        />
      )}
    </div>
  );
}

function ShareUrl({ snippetId }: { snippetId: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${basePath}/s/${snippetId}`;
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-9 font-mono text-xs"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // noop
        }
      }}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 mr-1.5" />
      ) : (
        <Copy className="w-3.5 h-3.5 mr-1.5" />
      )}
      {copied ? "Copied" : "Copy share link"}
    </Button>
  );
}
